import { useState } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { api } from '../../lib/api';

interface ModalSenhaGerencialProps {
  // A senha validada e repassada (opcional) pra quem precisa reprovar no backend
  // (ex: desconto > 5% no fechamento do caixa). Chamadores antigos ignoram o argumento.
  onConfirm: (senha?: string) => void;
  onCancel: () => void;
  // Cabecalho contextual (default = desconto > 5%, o chamador original). Quem reusa em outro
  // contexto (ex: estorno de cartao no cancelamento, DA-251) passa o seu.
  titulo?: string;
  subtitulo?: string;
}

export function ModalSenhaGerencial({
  onConfirm,
  onCancel,
  titulo = 'Desconto acima de 5%',
  subtitulo = 'Requer autorizacao gerencial',
}: ModalSenhaGerencialProps) {
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  // O campo nasce readOnly (vira editavel so quando o gerente clica) pra o navegador
  // NAO autopreencher aqui a senha salva de um admin/gerente — senao um estoquista abre
  // o modal logado e o Firefox/Chrome entregam a senha do gerente sozinho. O controle so
  // vale se a senha for digitada na hora. Ver project_senha_gerencial_sem_autofill.
  const [editavel, setEditavel] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      const res = await api.post<{ valida: boolean }>('/api/auth/verificar-senha-gerencial', { senha });
      if (res.valida) {
        onConfirm(senha);
      } else {
        setErro('Senha gerencial invalida');
      }
    } catch {
      setErro('Erro ao verificar senha');
    } finally {
      setLoading(false);
    }
  }

  // z-[1100]: senha gerencial e sempre pedida DE DENTRO de outro modal (cancelar venda /
  // desconto no fechamento, ambos .modal-overlay em z-1000). Em z-50 ela abria ATRAS e
  // "nada acontecia" ao clicar. Prompt de autorizacao tem que ser sempre o topo.
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm" style={{ position: 'relative' }}>
        <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={onCancel} aria-label="Fechar">×</button>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <AlertTriangle size={20} className="text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{titulo}</h3>
            <p className="text-sm text-gray-500">{subtitulo}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Senha Gerencial</label>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Informe a senha"
              readOnly={!editavel}
              onFocus={() => setEditavel(true)}
              autoComplete="new-password"
              name="fdc-autorizacao-gerencial"
              data-lpignore="true"
              data-1p-ignore=""
              data-form-type="other"
            />
          </div>

          {erro && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
              {erro}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !senha}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Autorizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
